import { animated, config, useSpring } from "@react-spring/web";
import { Box, Divider, IconButton } from "@sidelinesports/fan-design-system";
import { CancelIcon } from "@sidelinesports/icons";
import { clsx } from "clsx";
import React, {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  createContext,
  type ElementType,
  type ReactNode,
  useContext,
  useRef,
  useState,
} from "react";
import { Section } from "../../analytics/Section.tsx";
import { Portal } from "../Dialog/Portal";
import {
  useSlideoutFocus,
  useSlideoutKeyboardControls,
} from "../SlideoutPanel/hooks";
import { MenuItem } from "./MenuItem.tsx";
import styles from "./SlideoutMenu.module.css";

// Types
interface SlideoutMenuContextValue {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

interface SlideoutMenuRootProps {
  children: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface SlideoutMenuContentProps {
  children: ReactNode;
  className?: string;
  onFinishClose?: () => void;
}

type PolymorphicProps<T extends ElementType> = {
  as?: T;
  children?: ReactNode;
} & ComponentPropsWithoutRef<T>;

type SlideoutMenuTriggerProps<T extends ElementType = "button"> =
  PolymorphicProps<T>;
type SlideoutMenuCloseButtonProps<T extends ElementType = "button"> =
  PolymorphicProps<T>;

const SlideoutMenuContext = createContext<SlideoutMenuContextValue | undefined>(
  undefined,
);

const useSlideoutMenuContext = (): SlideoutMenuContextValue => {
  const context = useContext(SlideoutMenuContext);

  if (!context) {
    throw new Error(
      "useSlideoutMenuContext must be used within a SlideoutMenu component",
    );
  }

  return context;
};

/**
 * The root component for the SlideoutMenu. It manages the open state and provides
 * context to its children.
 *
 * It can be controlled via props or managed internally via the `SlideoutMenu.Trigger`
 * and `SlideoutMenu.CloseButton` components.
 */
const SlideoutMenuRoot: React.FC<SlideoutMenuRootProps> = ({
  children,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const handleOpenChange = (newOpen: boolean) => {
    if (controlledOpen === undefined) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  const contextValue: SlideoutMenuContextValue = {
    isOpen,
    onOpen: () => handleOpenChange(true),
    onClose: () => handleOpenChange(false),
  };

  return (
    <SlideoutMenuContext.Provider value={contextValue}>
      {children}
    </SlideoutMenuContext.Provider>
  );
};

const SlideoutMenuTrigger = <T extends ElementType = "button">({
  as,
  children,
  onClick,
  ...props
}: SlideoutMenuTriggerProps<T>) => {
  const Component = as || "button";
  const { onOpen } = useSlideoutMenuContext();

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    onClick?.(e);
    if (!e.defaultPrevented) {
      onOpen();
    }
  };

  return (
    <Component onClick={handleClick} {...props}>
      {children}
    </Component>
  );
};

const SlideoutMenuCloseButton = <T extends ElementType = "button">({
  as,
  children,
  onClick,
  ...props
}: SlideoutMenuCloseButtonProps<T>) => {
  const Component = as || DefaultCloseButton;
  const { onClose } = useSlideoutMenuContext();

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    onClick?.(e);
    if (!e.defaultPrevented) {
      onClose();
    }
  };

  return (
    <Component onClick={handleClick} {...props}>
      {children}
    </Component>
  );
};

const DefaultCloseButton = (props: ComponentProps<"button">) => (
  <IconButton
    aria-label="Close menu"
    variant="outline-light"
    size="lg"
    icon={<CancelIcon />}
    {...props}
  />
);

const SlideoutMenuContent: React.FC<SlideoutMenuContentProps> = ({
  children,
  className,
  onFinishClose,
}) => {
  const { isOpen, onClose } = useSlideoutMenuContext();
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedElement = useRef<Element | null>(null);

  // Spring animations for overlay and panel
  const overlayAnimation = useSpring({
    opacity: isOpen ? 1 : 0,
    pointerEvents: isOpen ? ("auto" as const) : ("none" as const),
    config: config.default,
    onRest: () => {
      if (!isOpen) {
        onFinishClose?.();
      }
    },
  });

  const panelAnimation = useSpring({
    transform: isOpen ? "translateX(0%)" : "translateX(-100%)",
    config: config.default,
  });

  const buttonAnimation = useSpring({
    opacity: isOpen ? 1 : 0,
    transform: isOpen ? "rotate(360deg)" : "rotate(0deg)",
    config: config.default,
  });

  useSlideoutFocus({
    isOpen,
    panelRef,
    previouslyFocusedElementRef: previouslyFocusedElement,
  });

  useSlideoutKeyboardControls({
    isOpen,
    panelRef,
    onClose,
  });

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  // We still render the Menu, even if it's closed. This allows us to
  // open up dialogs from inside the menu without having them unmount as soon as
  // the menu closes.

  return (
    <Portal root={document.body}>
      <animated.div
        className={styles.overlay}
        style={overlayAnimation}
        onClick={handleOverlayClick}
        aria-hidden={!isOpen}
      />
      <AnimatedBox
        ref={panelRef}
        bg="primary"
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className={clsx(styles.menu, className)}
        style={panelAnimation}
      >
        <Section name="Menu">{children}</Section>
      </AnimatedBox>
      <animated.div
        style={buttonAnimation}
        className={styles.closeButtonContainer}
        aria-hidden={!isOpen}
      >
        <SlideoutMenuCloseButton />
      </animated.div>
    </Portal>
  );
};

const AnimatedBox = animated(Box);

const SlideoutMenu = {
  Divider: () => (
    <Divider size="calc(100% + 2rem)" className={styles.divider} />
  ),
  Root: SlideoutMenuRoot,
  Trigger: SlideoutMenuTrigger,
  Content: SlideoutMenuContent,
  CloseButton: SlideoutMenuCloseButton,
  MenuItem: MenuItem,
};

export { SlideoutMenu, useSlideoutMenuContext };
